import { jsPDF } from "jspdf";

// ─── Tipos de ayuda ────────────────────────────────────────────────────────────

type FontStyle = "normal" | "bold" | "italic" | "bolditalic";

interface TextSegment {
  text: string;
  style: FontStyle;
}

// ─── Utilidades de renderizado ─────────────────────────────────────────────────

/**
 * Verifica si queda espacio suficiente en la página actual; si no, agrega una
 * nueva página y reinicia currentY.
 */
const checkPageBreak = (
  doc: jsPDF,
  currentY: number,
  neededHeight: number,
  marginY = 15
): number => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + neededHeight > pageHeight - marginY) {
    doc.addPage();
    return marginY + 5;
  }
  return currentY;
};

/**
 * Renderiza un párrafo de texto con soporte para segmentos bold/italic/normal
 * mezclados en la misma línea. Retorna el nuevo currentY.
 */
const renderMixedLine = (
  doc: jsPDF,
  segments: TextSegment[],
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number,
  lineHeight: number
): number => {
  // Construir el texto plano completo para saber cuántas líneas ocupa
  const fullText = segments.map((s) => s.text).join("");
  doc.setFontSize(fontSize);
  doc.setFont("Helvetica", "normal");
  const lines = doc.splitTextToSize(fullText, maxWidth) as string[];

  // Renderizar línea a línea con estilos aplicados por segmento
  let charCursor = 0;
  lines.forEach((line) => {
    let cursorX = x;
    let remaining = line;
    let segIdx = 0;
    let segCharOffset = 0;

    // Calcular cuántos caracteres de cada segmento caen en esta línea
    while (remaining.length > 0 && segIdx < segments.length) {
      const seg = segments[segIdx];
      const available = seg.text.length - segCharOffset;
      const take = Math.min(available, remaining.length);
      const chunk = seg.text.slice(segCharOffset, segCharOffset + take);

      doc.setFont("Helvetica", seg.style);
      doc.setFontSize(fontSize);
      doc.text(chunk, cursorX, y);
      cursorX += doc.getTextWidth(chunk);

      remaining = remaining.slice(take);
      segCharOffset += take;

      if (segCharOffset >= seg.text.length) {
        segIdx++;
        segCharOffset = 0;
      }
    }

    charCursor += line.length;
    y += lineHeight;
  });

  return y;
};

/**
 * Renderiza un bloque de texto normal (sin mezcla de estilos), con salto de
 * página automático. Retorna el nuevo currentY.
 */
const renderText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number,
  fontStyle: FontStyle,
  lineHeight: number,
  color: string,
  marginY = 15
): number => {
  doc.setFontSize(fontSize);
  doc.setFont("Helvetica", fontStyle);
  doc.setTextColor(color);
  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  lines.forEach((line) => {
    y = checkPageBreak(doc, y, lineHeight, marginY);
    doc.text(line, x, y);
    y += lineHeight;
  });
  return y;
};

/**
 * Dibuja una línea horizontal divisoria y retorna el nuevo currentY.
 */
const drawHRule = (
  doc: jsPDF,
  y: number,
  marginX: number,
  pageWidth: number,
  color: string,
  weight = 0.3
): number => {
  doc.setDrawColor(color);
  doc.setLineWidth(weight);
  doc.line(marginX, y, pageWidth - marginX, y);
  return y + 5;
};

// ─── Generador principal ───────────────────────────────────────────────────────

/**
 * Genera un PDF del contrato de alquiler de bicicletas fiel al contenido de
 * public/files/contract.md, personalizado con los datos del cliente.
 */
export const generateContractPdf = async (customer: {
  name: string;
  idCard: number;
  phone: string;
  idPhotoUrl?: string | null;
}): Promise<Blob> => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // ── Paleta ──────────────────────────────────────────────────────────────────
  const primaryColor   = "#0f172a"; // slate-900
  const accentColor    = "#0ea5e9"; // sky-500
  const secondaryColor = "#475569"; // slate-600
  const borderColor    = "#cbd5e1"; // slate-300
  const bgBoxColor     = "#f8fafc"; // slate-50
  const quoteColor     = "#64748b"; // slate-500

  // ── Layout ──────────────────────────────────────────────────────────────────
  const marginX     = 18;
  const marginY     = 15;
  const indentL1    = marginX + 5;   // cláusulas
  const indentL2    = marginX + 10;  // sub-items
  const indentL3    = marginX + 14;  // cita/blockquote
  const pageWidth   = doc.internal.pageSize.getWidth();
  const contentW    = pageWidth - marginX * 2;
  const contentWL1  = pageWidth - indentL1 - marginX;
  const contentWL2  = pageWidth - indentL2 - marginX;
  const contentWL3  = pageWidth - indentL3 - marginX;

  const LH_TITLE    = 7;
  const LH_BODY     = 4.8;
  const LH_SMALL    = 4.3;

  let y = 20;

  // ── Encabezado con logo/marca ───────────────────────────────────────────────
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(primaryColor);
  doc.text("PEDAL POINT", marginX, y);

  doc.setFontSize(9);
  doc.setFont("Helvetica", "normal");
  doc.setTextColor(secondaryColor);
  doc.text("Sistema de Gestión de Alquileres", marginX, y + 5.5);

  // RIF y REF alineados a la derecha
  const now = new Date();
  const dateStr = now.toLocaleDateString("es-VE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const rightX = pageWidth - marginX;
  doc.setFontSize(8);
  doc.setTextColor(secondaryColor);
  doc.text(`Fecha: ${dateStr}`, rightX, y, { align: "right" });
  doc.text("RIF: J-50240760-0", rightX, y + 4.5, { align: "right" });

  y += 12;

  // Línea de acento principal
  doc.setDrawColor(accentColor);
  doc.setLineWidth(0.9);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 7;

  // ── Título del contrato ─────────────────────────────────────────────────────
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(primaryColor);
  doc.text("CONTRATO DE ALQUILER DE BICICLETAS", pageWidth / 2, y, { align: "center" });
  y += LH_TITLE;

  // Subtítulo GO ENBICI → GO PEDAL POINT
  doc.setFont("Helvetica", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(accentColor);
  doc.text(
    "Al momento de solicitar nuestros servicios, estás aceptando las políticas y condiciones del presente contrato.",
    pageWidth / 2,
    y,
    { align: "center" }
  );
  y += 8;

  // ── Párrafo introductorio con datos del cliente ─────────────────────────────
  doc.setFont("Helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(primaryColor);

  const intro =
    `Entre la empresa ENBICI.CA, No de RIF J-502407600, con domicilio fiscal en calle principal, vereda 1, casa número 9, urb Altos de Gallardin, Palo Gordo, ` +
    `Municipio Cárdenas, Estado Táchira, quien se denominará EL PROPIETARIO, y el ciudadano(a), ${customer.name.toUpperCase()} con cédula de identidad No. V-${customer.idCard}, ` +
    `y número de teléfono ${customer.phone}, el cual se denominará EL USUARIO, ` +
    `se procede a realizar el presente CONTRATO DE ALQUILER DE BICICLETAS, bajo las ` +
    `condiciones y los términos que se describen a continuación:`;

  y = renderText(doc, intro, marginX, y, contentW, 9.5, "normal", LH_BODY, primaryColor, marginY);
  y += 4;

  // ── Caja de datos del cliente ───────────────────────────────────────────────
  y = checkPageBreak(doc, y, 36, marginY);
  doc.setDrawColor(borderColor);
  doc.setFillColor(bgBoxColor);
  doc.roundedRect(marginX, y, contentW, 34, 2, 2, "FD");

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(accentColor);
  doc.text("DATOS DEL USUARIO / ARRENDATARIO", marginX + 5, y + 7);

  doc.setFontSize(9);
  doc.setTextColor(secondaryColor);
  doc.setFont("Helvetica", "normal");
  const labelX = marginX + 5;
  const valueX = marginX + 52;
  doc.text("Nombre Completo:", labelX, y + 15);
  doc.text("Cédula de Identidad:", labelX, y + 22);
  doc.text("Teléfono:", labelX, y + 29);

  doc.setFont("Helvetica", "bold");
  doc.setTextColor(primaryColor);
  doc.text(customer.name, valueX, y + 15);
  doc.text(`V-${customer.idCard}`, valueX, y + 22);
  doc.text(customer.phone, valueX, y + 29);

  y += 40;

  // ── Separador ──────────────────────────────────────────────────────────────
  y = drawHRule(doc, y, marginX, pageWidth, borderColor);
  y += 2;

  // ── Cláusulas del Contrato ──────────────────────────────────────────────────
  y = checkPageBreak(doc, y, LH_TITLE + 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(primaryColor);
  doc.text("Cláusulas del Contrato", marginX, y);
  y += LH_TITLE;

  const clauses: string[] = [
    "1.- EL PROPIETARIO es el único dueño de las bicicletas objeto del presente contrato, las cuales se ofrecen para el servicio de alquiler.",
    "2.- EL USUARIO quien puede ser Persona Natural o Jurídica, interesada en hacer uso del servicio de alquiler, es responsable del cuidado y devolución de la(s) bicicleta(s) y de todos sus accesorios (Casco) y se obliga a cancelar los pagos o abono de las tarifas correspondientes de acuerdo a las políticas y normas de EL PROPIETARIO y que declara conocer.",
    "3.- Las bicicletas se encuentran en perfecto estado para su funcionamiento y cumplen con todas las medidas de seguridad y las garantías comerciales exigibles y han de ser devueltas en el mismo estado en el que se recibieron al ser alquiladas, haciéndose cargo EL USUARIO de cualquier desperfecto ocasionado durante sus usos.",
    "4.- EL USUARIO está obligado en todo momento a vigilar y proteger correctamente la(s) bicicleta(s) así como el (los) casco (s) que se le hace entrega como accesorios, los cuales deben ser utilizados para seguridad de EL USUARIO y/o las personas que a su nombre utilicen nuestros servicios de alquiler, incluyendo los menores de edad los cuales deberán ser representados por EL USUARIO en nuestras instalaciones al momento de solicitar servicios.",
    "5.- En caso de robo, hurto, destrucción o daños sobre la(s) bicicleta(s) y sus accesorios, EL USUARIO será el único responsable y tendrá la obligación de denunciar los hechos ante las autoridades competentes. Asimismo, asumirá el costo de reposición y/o de las reparaciones necesarias según sea el caso.",
  ];

  doc.setFont("Helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(primaryColor);

  clauses.forEach((clause) => {
    y = checkPageBreak(doc, y, LH_BODY * 2, marginY);
    const split = doc.splitTextToSize(`${clause}`, contentWL1) as string[];
    split.forEach((line, i) => {
      y = checkPageBreak(doc, y, LH_BODY, marginY);
      doc.text(line, i === 0 ? indentL1 : indentL1 + 3.5, y);
      y += LH_BODY;
    });
    y += 1.5;
  });

  // Cláusula 6 (con cita del Código Penal)
  y = checkPageBreak(doc, y, LH_BODY * 3, marginY);
  const clause6a =
    "6.- En caso de que EL USUARIO no devolviera la bicicleta, en la hora que le corresponde entregarla, EL PROPIETARIO, " +
    "esperará un lapso de Doce (12) horas continuas para realizar la respectiva denuncia a los cuerpos de seguridad " +
    "competentes, imputándole el Delito de Apropiación Indebida tipificado en el Artículo 466 del Código Penal Venezolano:";

  const split6a = doc.splitTextToSize(`${clause6a}`, contentWL1) as string[];
  split6a.forEach((line, i) => {
    y = checkPageBreak(doc, y, LH_BODY, marginY);
    doc.text(line, i === 0 ? indentL1 : indentL1 + 3.5, y);
    y += LH_BODY;
  });
  y += 2;

  // Blockquote — cita del art. 466
  y = checkPageBreak(doc, y, LH_SMALL * 4 + 4, marginY);

  const quoteStartBy = y;
  doc.setFont("Helvetica", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(quoteColor);

  const quoteText =
    '"El que se haya apropiado, en beneficio propio o de otro, alguna cosa ajena que se le hubiere confiado o entregado ' +
    "por cualquier título que comporte la obligación de restituirla o de hacer de ella un uso determinado, será castigado " +
    'con prisión de tres meses a dos años, por acusación de la parte agraviada."';

  const splitQuote = doc.splitTextToSize(quoteText, contentWL2) as string[];

  splitQuote.forEach((line) => {
    y = checkPageBreak(doc, y, LH_SMALL, marginY);
    doc.text(line, indentL3, y);
    y += LH_SMALL;
  });

  doc.setDrawColor(accentColor);
  doc.setLineWidth(0.6);
  //doc.line(indentL2, quoteStartBy - 1, indentL2, y - LH_SMALL + 2);
  doc.line(indentL2, quoteStartBy - 5, indentL2, y - LH_SMALL + 2);

  y += 2;

  const clause6b =
    "EL PROPIETARIO se reserva el derecho del cobro íntegro del valor de la(s) bicicleta(s), así como la indemnización " +
    "de los daños y perjuicios ocasionados.";
  y = renderText(doc, clause6b, indentL1 + 3.5, y, contentWL1, 9, "normal", LH_BODY, primaryColor, marginY);
  y += 2;

  // Cláusulas 7-12
  const clauses7to12: string[] = [
    "7.- El retraso en la devolución de la(s) bicicleta(s) supondrá un recargo adicional de 3 dólares en Bolívares ó moneda extranjera en base a la tarifa establecida para ese acto por EL PROPIETARIO convertidos estos últimos, en moneda nacional de curso legal de acuerdo a la tasa del Banco Central de Venezuela si fuera el caso por cada hora de retraso y por cada bicicleta(s), ya que se presume que la(s) bicicleta(s) estén a disposición de EL USUARIO.",
    "8.- EL USUARIO de la(s) bicicleta(s) se compromete a respetar las leyes y normas de tránsito vigentes y es el único responsable de todo daño causado como consecuencia de cualquier accidente por imprudencia, negligencia o impericia durante el uso de la bicicleta. Asimismo, EL PROPIETARIO no se hace responsable de ninguna multa, sanción o accidente derivado de cualquier infracción que pueda cometer EL USUARIO, ya que éste conoce los riesgos que implica conducir una bicicleta y es el único responsable de los daños causados a sí mismo, a terceras personas o a cualquier bien, mueble o inmueble, por la utilización o uso normal o anormal de las bicicletas.",
    "9.- EL PROPIETARIO, no se hace responsable de reclamaciones por daños corporales o materiales ocasionados a sí mismo o a terceras personas por parte de EL USUARIO o a objetos de su propiedad.",
    "10.- EL USUARIO acepta los términos y condiciones del presente contrato al momento de recibir el servicio, sin perjuicio de lo dispuesto en los artículos contemplados en la LEY SOBRE MENSAJES DE DATOS Y FIRMAS ELECTRÓNICAS.",
    "11.- El presente contrato reposará en los archivos digitales de EL PROPIETARIO junto a los registros de EL USUARIO disponibles tanto para él como para cualquier medio probatorio solicitado por entes gubernamentales de acuerdo a la ley.",
    "12.- EL PROPIETARIO enviará copia del presente contrato al correo electrónico del USUARIO a solicitud del mismo. Sin embargo, EL USUARIO ha leído y aceptado las condiciones de este contrato y sus políticas de servicio, al momento en que solicitó por primera vez los servicios de EL PROPIETARIO de acuerdo a nuestros registros, los cuales estarán a disposición de los organismos competentes en actos públicos pautados en las leyes. El USUARIO no podrá alegar el desconocimiento de nuestras políticas de servicios, ya que siempre estarán a disposición de las partes tanto en las oficinas físicas como en enlaces de internet disponibles en sitios web, redes sociales entre otros y que servirán como instrumentos probatorios de acuerdo a la LEY SOBRE MENSAJES DE DATOS Y FIRMAS ELECTRÓNICAS vigente.",
  ];

  doc.setFont("Helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(primaryColor);

  clauses7to12.forEach((clause) => {
    y = checkPageBreak(doc, y, LH_BODY * 2, marginY);
    const split = doc.splitTextToSize(`${clause}`, contentWL1) as string[];
    split.forEach((line, i) => {
      y = checkPageBreak(doc, y, LH_BODY, marginY);
      doc.text(line, i === 0 ? indentL1 : indentL1 + 3.5, y);
      y += LH_BODY;
    });
    y += 1.5;
  });

  // ── Separador ──────────────────────────────────────────────────────────────
  y += 2;
  y = checkPageBreak(doc, y, 10, marginY);
  y = drawHRule(doc, y, marginX, pageWidth, borderColor);
  y += 2;

  // ── Extracto de la Ley sobre Mensajes de Datos y Firmas Electrónicas ────────
  y = checkPageBreak(doc, y, LH_TITLE + 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(primaryColor);
  doc.text("Extracto de la Ley sobre Mensajes de Datos y Firmas Electrónicas", marginX, y);
  y += LH_TITLE + 1;

  // CAPÍTULO I
  y = checkPageBreak(doc, y, LH_BODY + 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(accentColor);
  doc.text("CAPITULO I", marginX, y);
  y += LH_BODY + 1;

  // Art. 1
  const art1Label = "Artículo 1. ";
  const art1Text =
    '"El presente Decreto-Ley tiene por objeto otorgar y reconocer eficacia y valor jurídico a la Firma Electrónica, ' +
    "al Mensaje de Datos y a toda información inteligible en formato electrónico, independientemente de su soporte " +
    "material, atribuible a personas naturales o jurídicas, públicas o privadas, así como regular todo lo relativo a " +
    "los Proveedores de Servicios de Certificación y los Certificados Electrónicos. El presente Decreto-Ley será " +
    "aplicable a los Mensajes de Datos y Firmas Electrónicas independientemente de sus características tecnológicas o " +
    "de los desarrollos tecnológicos que se produzcan en un futuro. A tal efecto, sus normas serán desarrolladas e " +
    "interpretadas progresivamente, orientadas a reconocer la validez y eficacia probatoria de los Mensajes de datos y " +
    "Firmas Electrónicas. La certificación a que se refiere el presente Decreto-Ley no excluye el cumplimiento de las " +
    'formalidades de registro público o autenticación que, de conformidad con la ley, requieran determinados actos o negocios jurídicos."';

  y = checkPageBreak(doc, y, LH_SMALL * 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const labelW1 = doc.getTextWidth(`• ${art1Label}`);
  doc.text(`• ${art1Label}`, indentL1, y);
  doc.setFont("Helvetica", "italic");
  doc.setTextColor(quoteColor);
  const splitArt1 = doc.splitTextToSize(art1Text, contentWL1 - labelW1) as string[];
  doc.text(splitArt1[0], indentL1 + labelW1, y);
  y += LH_SMALL;
  if (splitArt1.length > 1) {
    const rest = splitArt1.slice(1).join(" ");
    const splitRest = doc.splitTextToSize(rest, contentWL1) as string[];
    splitRest.forEach((line) => {
      y = checkPageBreak(doc, y, LH_SMALL, marginY);
      doc.text(line, indentL1 + 3.5, y);
      y += LH_SMALL;
    });
  }
  y += 2;

  // Art. 2
  y = checkPageBreak(doc, y, LH_SMALL * 2, marginY);
  const art2Label = "Artículo 2. ";
  const art2Intro =
    '"A los efectos del presente Decreto-Ley, se entenderá por:';
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const labelW2 = doc.getTextWidth(`• ${art2Label}`);
  doc.text(`• ${art2Label}`, indentL1, y);
  doc.setFont("Helvetica", "italic");
  doc.setTextColor(quoteColor);
  doc.text(art2Intro, indentL1 + labelW2, y);
  y += LH_SMALL + 1;

  const art2Items: [string, string][] = [
    ["Persona:", "Todo sujeto jurídicamente hábil, bien sea natural, jurídica, pública, privada, nacional o extranjera, susceptible de adquirir derechos y contraer obligaciones."],
    ["Mensajes de datos:", "Toda información inteligible en formato electrónico o similar que pueda ser almacenada o intercambiada por cualquier medio."],
    ["Emisor:", "Persona que origina un Mensaje de Datos por sí mismo, o a través de terceros autorizados."],
    ["Firma Electrónica:", "Información creada o utilizada por el Signatario, asociada al Mensaje de Datos, que permite atribuirle su autoría bajo el contexto en el cual ha sido empleado."],
    ["Signatario:", "Es la persona titular de una Firma Electrónica o Certificado Electrónico."],
    ["Destinatario:", "Persona a quien va dirigido el Mensaje de Datos."],
    ["Proveedor de Servicios de Certificación:", "Persona dedicada a proporcionar Certificados Electrónicos y demás actividades previstas en este Decreto-Ley."],
    ["Certificado Electrónico:", "Mensaje de Datos proporcionado por un Proveedor de Servicios de Certificación que le atribuye certeza y validez a la Firma Electrónica."],
    ["Sistema de Información:", "Aquel utilizado para generar, procesar o archivar de cualquier forma Mensajes de Datos."],
    ["Usuario:", "Toda persona que utilice un sistema de información."],
    ["Inhabilitación técnica:", "Es la incapacidad temporal o permanente del Proveedor de Servicios de Certificación que impida garantizar el cumplimiento de sus servicios, así como, cumplir con los requisitos y condiciones establecidos en este Decreto-Ley para el ejercicio de sus actividades."],
  ];

  art2Items.forEach(([label, text]) => {
    y = checkPageBreak(doc, y, LH_SMALL * 2, marginY);
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(primaryColor);
    const lw = doc.getTextWidth(`  – ${label} `);
    doc.text(`  – ${label} `, indentL2, y);
    doc.setFont("Helvetica", "italic");
    doc.setTextColor(quoteColor);
    const splitItem = doc.splitTextToSize(text, contentWL2 - lw) as string[];
    doc.text(splitItem[0], indentL2 + lw, y);
    y += LH_SMALL;
    if (splitItem.length > 1) {
      const rest = splitItem.slice(1).join(" ");
      const splitRest = doc.splitTextToSize(rest, contentWL2) as string[];
      splitRest.forEach((l) => {
        y = checkPageBreak(doc, y, LH_SMALL, marginY);
        doc.text(l, indentL2 + 2, y);
        y += LH_SMALL;
      });
    }
  });

  // Nota final Art. 2
  y += 1;
  const art2End =
    'El reglamento del presente Decreto-Ley podrá adaptar las definiciones antes señaladas a los desarrollos ' +
    'tecnológicos que se produzcan en el futuro. Así mismo, podrá establecer otras definiciones que fueren necesarias ' +
    'para la eficaz aplicación de este Decreto-Ley."';
  y = renderText(doc, art2End, indentL2, y, contentWL2, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  y += 3;

  // CAPÍTULO II
  y = checkPageBreak(doc, y, LH_BODY + 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(accentColor);
  doc.text("CAPITULO II", marginX, y);
  y += LH_BODY + 1;

  // Art. 4
  const art4Label = "Artículo 4. ";
  const art4Text =
    '"Los Mensajes de Datos tendrán la misma eficacia probatoria que la ley otorga a los documentos escritos, sin ' +
    "perjuicio de lo establecido en la primera parte del artículo 6 de este Decreto-Ley. Su promoción, control, " +
    "contradicción y evacuación como medio de prueba, se realizará conforme a lo previsto para las pruebas libres en " +
    "el Código de Procedimiento Civil. La información contenida en un Mensaje de Datos, reproducida en formato " +
    'impreso, tendrá la misma eficacia probatoria atribuida en la ley a las copias o reproducciones fotostáticas."';

  y = checkPageBreak(doc, y, LH_SMALL, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const lw4 = doc.getTextWidth(`• ${art4Label}`);
  doc.text(`• ${art4Label}`, indentL1, y);
  doc.setFont("Helvetica", "italic");
  doc.setTextColor(quoteColor);
  const split4 = doc.splitTextToSize(art4Text, contentWL1 - lw4) as string[];
  doc.text(split4[0], indentL1 + lw4, y);
  y += LH_SMALL;
  if (split4.length > 1) {
    const rest = split4.slice(1).join(" ");
    y = renderText(doc, rest, indentL1 + 3.5, y, contentWL1, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  }
  y += 2;

  // Art. 5
  const art5Label = "Artículo 5. ";
  const art5Text =
    '"Los Mensajes de Datos estarán sometidos a las disposiciones constitucionales y legales que garantizan los ' +
    'derechos a la privacidad de las comunicaciones y de acceso a la información personal."';
  y = checkPageBreak(doc, y, LH_SMALL, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const lw5 = doc.getTextWidth(`• ${art5Label}`);
  doc.text(`• ${art5Label}`, indentL1, y);
  doc.setFont("Helvetica", "italic");
  doc.setTextColor(quoteColor);
  const split5 = doc.splitTextToSize(art5Text, contentWL1 - lw5) as string[];
  doc.text(split5[0], indentL1 + lw5, y);
  y += LH_SMALL;
  if (split5.length > 1) {
    const rest = split5.slice(1).join(" ");
    y = renderText(doc, rest, indentL1 + 3.5, y, contentWL1, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  }
  y += 2;

  // Art. 8
  y = checkPageBreak(doc, y, LH_SMALL, marginY);
  const art8Label = "Artículo 8. ";
  const art8Intro =
    '"Cuando la ley requiera que la información conste por escrito, ese requisito quedará satisfecho con relación ' +
    "a un Mensaje de Datos, si la información que éste contiene es accesible para su ulterior consulta. Cuando la " +
    "ley requiera que ciertos actos o negocios jurídicos consten por escrito y su soporte deba permanecer accesible, " +
    "conservado o archivado por un período determinado o en forma permanente, estos requisitos quedarán satisfechos " +
    "mediante la conservación de los Mensajes de Datos, siempre que se cumplan las siguiente condiciones:";
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const lw8 = doc.getTextWidth(`• ${art8Label}`);
  doc.text(`• ${art8Label}`, indentL1, y);
  doc.setFont("Helvetica", "italic");
  doc.setTextColor(quoteColor);
  const split8 = doc.splitTextToSize(art8Intro, contentWL1 - lw8) as string[];
  doc.text(split8[0], indentL1 + lw8, y);
  y += LH_SMALL;
  if (split8.length > 1) {
    const rest = split8.slice(1).join(" ");
    y = renderText(doc, rest, indentL1 + 3.5, y, contentWL1, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  }
  y += 1;

  const art8Items: string[] = [
    "Que la información que contengan pueda ser consultada posteriormente.",
    "Que conserven el formato en que se generó, archivó o recibió o en algún formato que sea demostrable que reproduce con exactitud la información generada o recibida.",
    "Que se conserve todo dato que permita determinar el origen y el destino del Mensaje de Datos, la fecha y la hora en que fue enviado o recibido.",
  ];
  art8Items.forEach((item, i) => {
    y = checkPageBreak(doc, y, LH_SMALL, marginY);
    doc.setFont("Helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(quoteColor);
    const numbered = `${i + 1}. ${item}`;
    const splitItem = doc.splitTextToSize(numbered, contentWL2) as string[];
    splitItem.forEach((line) => {
      y = checkPageBreak(doc, y, LH_SMALL, marginY);
      doc.text(line, indentL2, y);
      y += LH_SMALL;
    });
  });
  y += 1;
  const art8End = 'Toda persona podrá recurrir a los servicios de un tercero para dar cumplimiento a los requisitos señalados en este artículo."';
  y = renderText(doc, art8End, indentL1 + 3.5, y, contentWL1, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  y += 3;

  // CAPÍTULO III
  y = checkPageBreak(doc, y, LH_BODY + 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(accentColor);
  doc.text("CAPITULO III - DE LA EMISIÓN Y RECEPCIÓN DE LOS MENSAJES DE DATOS", marginX, y);
  y += LH_BODY + 1;

  // Art. 9
  const art9Label = "Artículo 9. ";
  const art9Intro =
    '"Las partes podrán acordar un procedimiento para establecer cuándo el Mensaje de Datos proviene efectivamente ' +
    "del Emisor. A falta de acuerdo entre las partes, se entenderá que un Mensajes de Datos proviene del Emisor, " +
    "cuando éste ha sido enviado por:";
  y = checkPageBreak(doc, y, LH_SMALL, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const lw9 = doc.getTextWidth(`• ${art9Label}`);
  doc.text(`• ${art9Label}`, indentL1, y);
  doc.setFont("Helvetica", "italic");
  doc.setTextColor(quoteColor);
  const split9 = doc.splitTextToSize(art9Intro, contentWL1 - lw9) as string[];
  doc.text(split9[0], indentL1 + lw9, y);
  y += LH_SMALL;
  if (split9.length > 1) {
    const rest = split9.slice(1).join(" ");
    y = renderText(doc, rest, indentL1 + 3.5, y, contentWL1, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  }
  y += 1;

  const art9Items = [
    "El propio Emisor.",
    "Persona autorizada para actuar en nombre del Emisor respecto de ese mensaje.",
    'Por un Sistema de Información programado por el Emisor, o bajo su autorización, para que opere automáticamente."',
  ];
  art9Items.forEach((item, i) => {
    y = checkPageBreak(doc, y, LH_SMALL, marginY);
    doc.setFont("Helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(quoteColor);
    y = renderText(doc, `${i + 1}. ${item}`, indentL2, y, contentWL2, 8.5, "italic", LH_SMALL, quoteColor, marginY);
  });
  y += 3;

  // ── Separador ──────────────────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 10, marginY);
  y = drawHRule(doc, y, marginX, pageWidth, borderColor);
  y += 2;

  // ── Cláusulas Finales (13-15) ───────────────────────────────────────────────
  y = checkPageBreak(doc, y, LH_TITLE + 2, marginY);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(primaryColor);
  doc.text("Cláusulas Finales", marginX, y);
  y += LH_TITLE;

  const finalClauses: string[] = [
    "13.- El presente contrato será único ejemplar y servirá para aplicarse en cualquier servicio de alquiler que EL USUARIO solicite en fechas posteriores. EL USUARIO declara conocer por información directa o indirecta, sobre las políticas de servicio de EL PROPIETARIO. Igualmente EL USUARIO declara que los datos personales suministrados al momento del registro son fidedignos, por lo que autoriza por este medio a EL PROPIETARIO, para verificar por cualquier medio lícito incluyendo medios a través de terceros, la veracidad de los mismos y está de acuerdo en el intercambio de envío de información a los medios digitales como el correo electrónico suministrado por él entre otros.",
    "14.- La información detallada de cada servicio de alquiler por parte de EL USUARIO será proporcionada por la aplicación de control de EL PROPIETARIO.",
    "15.- Para la resolución de cualquier controversia o conflicto surgido en la aplicación del presente contrato, se elige como domicilio especial y principal, a la ciudad de San Cristóbal - Edo. Táchira, a cuyos Tribunales las partes declaran expresamente someterse.",
  ];

  doc.setFont("Helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(primaryColor);

  finalClauses.forEach((clause) => {
    y = checkPageBreak(doc, y, LH_BODY * 2, marginY);
    const split = doc.splitTextToSize(`${clause}`, contentWL1) as string[];
    split.forEach((line, i) => {
      y = checkPageBreak(doc, y, LH_BODY, marginY);
      doc.text(line, i === 0 ? indentL1 : indentL1 + 3.5, y);
      y += LH_BODY;
    });
    y += 2;
  });

  y += 4;

  // ── Sección de Foto de Cédula ───────────────────────────────────────────────
  if (customer.idPhotoUrl) {
    try {
      y = checkPageBreak(doc, y, 60, marginY);
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryColor);
      doc.text("DOCUMENTO DE IDENTIFICACIÓN (CÉDULA DE IDENTIDAD)", marginX, y);
      y += 6;

      const imgWidth  = 75;
      const imgHeight = 48;

      let imgData = customer.idPhotoUrl;
      if (!imgData.startsWith("data:image")) {
        imgData = await new Promise<string>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width  = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/jpeg"));
            } else {
              reject(new Error("No se pudo obtener el contexto del Canvas"));
            }
          };
          img.onerror = () => reject(new Error("Error al cargar la imagen de la cédula"));
          img.src = customer.idPhotoUrl!;
        });
      }

      doc.setDrawColor(borderColor);
      doc.setFillColor("#ffffff");
      doc.rect(marginX - 1, y - 1, imgWidth + 2, imgHeight + 2, "D");
      doc.addImage(imgData, "JPEG", marginX, y, imgWidth, imgHeight);
      y += imgHeight + 8;
    } catch (e) {
      console.error("Error al renderizar foto de cédula en PDF:", e);
      doc.setFont("Helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor("#ef4444");
      doc.text("[La foto de la cédula no pudo cargarse en este documento]", marginX, y);
      y += 8;
    }
  }

  // ── Separador ──────────────────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 35, marginY);
  y = drawHRule(doc, y, marginX, pageWidth, borderColor);
  y += 4;

  // ── Firma del Cliente ───────────────────────────────────────────────────────
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(primaryColor);
  doc.text("FIRMA DEL CLIENTE", marginX, y);
  y += 8;

  // Línea de firma
  doc.setDrawColor(primaryColor);
  doc.setLineWidth(0.4);
  doc.line(marginX, y, marginX + 80, y);
  y += 5;
  doc.setFont("Helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(secondaryColor);
  doc.text(customer.name, marginX, y);
  y += 4;
  doc.text(`C.I.: V-${customer.idCard}`, marginX, y);
  y += 8;

  // Aceptación digital
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(primaryColor);
  const acceptanceText =
    `Aceptado digitalmente por el cliente el ${now.toLocaleDateString("es-VE")} a las ${now.toLocaleTimeString("es-VE")}.`;
  doc.text(acceptanceText, marginX, y);
  y += 5;

  doc.setFont("Helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(secondaryColor);
  const footerText =
    "Este contrato es emitido electrónicamente por el sistema Pedal Point Rental Manager y es vinculante a partir " +
    "del registro del cliente, de acuerdo a la Ley sobre Mensajes de Datos y Firmas Electrónicas vigente.";
  const splitFooter = doc.splitTextToSize(footerText, contentW) as string[];
  splitFooter.forEach((line) => {
    doc.text(line, marginX, y);
    y += 4;
  });

  // Numeración de páginas
  const totalPages = (doc.internal as unknown as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(secondaryColor);
    doc.text(
      `Página ${i} de ${totalPages}  |  EnBici — Contrato de Alquiler de Bicicletas`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: "center" }
    );
  }

  return doc.output("blob");
};
